"use client"

import {
  type CSSProperties,
  type FocusEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react"
import cn from "classnames"
import AutoScroll, { type AutoScrollType } from "embla-carousel-auto-scroll"
import useEmblaCarousel from "embla-carousel-react"

import { useMatchMedia } from "~/hooks/useMatchMedia"
import { getModuleConfigProps } from "~/utils/moduleConfig"

import type { PeopleHeroInterface } from "./PeopleHero.interface"
import s from "./PeopleHero.module.scss"
import { PeopleHeroCardItem } from "./PeopleHeroCard"
import { PeopleHeroTicker } from "./PeopleHeroTicker"

const MIN_RENDERED_CAROUSEL_CARDS = 12

export type PeopleHeroViewProps = Omit<PeopleHeroInterface, "id"> & {
  moduleIndex: number
  className?: string
  style?: CSSProperties
}

export const PeopleHeroView = ({
  headlines,
  cards,
  config,
  moduleIndex,
  className,
  style,
}: PeopleHeroViewProps) => {
  const configProps = getModuleConfigProps(config)
  const prefersReducedMotion = useMatchMedia("(prefers-reduced-motion: reduce)")
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: "center",
      containScroll: false,
      loop: true,
      skipSnaps: false,
    },
    [
      AutoScroll({
        playOnInit: !prefersReducedMotion,
        speed: 1,
        stopOnInteraction: true,
        stopOnMouseEnter: false,
        stopOnFocusIn: false,
      }),
    ],
  )
  const [activeCardIndex, setActiveCardIndex] = useState(-1)
  const [isCarouselPaused, setIsCarouselPaused] = useState(false)

  const hasFocusWithin = useRef(false)
  const heroRef = useCallback((node: HTMLElement | null) => {
    if (node) {
      const isFirstChild = node.parentElement?.firstElementChild === node
      if (isFirstChild) {
        node.parentElement?.parentElement?.setAttribute("data-has-hero", "true")
      }
    }
  }, [])

  const pauseCarousel = useCallback(() => setIsCarouselPaused(true), [])

  const resumeCarousel = useCallback(() => {
    if (!hasFocusWithin.current) setIsCarouselPaused(false)
  }, [])

  const handleCarouselFocus = useCallback(() => {
    hasFocusWithin.current = true
    setIsCarouselPaused(true)
  }, [])

  const handleCarouselBlur = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      const nextFocusedElement = event.relatedTarget as Node | null

      if (
        nextFocusedElement &&
        event.currentTarget.contains(nextFocusedElement)
      ) {
        return
      }

      hasFocusWithin.current = false
      resumeCarousel()
    },
    [resumeCarousel],
  )

  useEffect(() => {
    if (!emblaApi) return

    emblaApi.on("pointerDown", pauseCarousel)
    emblaApi.on("pointerUp", resumeCarousel)

    return () => {
      emblaApi.off("pointerDown", pauseCarousel)
      emblaApi.off("pointerUp", resumeCarousel)
    }
  }, [emblaApi, pauseCarousel, resumeCarousel])

  useEffect(() => {
    const autoScroll = emblaApi?.plugins()?.autoScroll as
      | AutoScrollType
      | undefined
    if (!autoScroll) return

    const shouldPlay =
      !prefersReducedMotion && !isCarouselPaused && cards.length >= 2

    if (shouldPlay) {
      autoScroll.play()
    } else {
      autoScroll.stop()
    }
  }, [cards.length, emblaApi, isCarouselPaused, prefersReducedMotion])

  const repeatedCards = Array.from(
    {
      length:
        cards.length > 0
          ? Math.max(1, Math.ceil(MIN_RENDERED_CAROUSEL_CARDS / cards.length))
          : 0,
    },
    () => cards,
  ).flat()

  return (
    <section
      ref={heroRef}
      className={cn(s.peopleHero, `stack-module-${moduleIndex}`, className)}
      data-module="people-hero"
      aria-label="People Hero Gallery"
      {...configProps}
      style={{ ...configProps.style, ...style }}
    >
      <div className={cn(s.content, "container", "default-grid")}>
        <h1 className={s.srOnly}>People Hero Gallery</h1>
        <PeopleHeroTicker headlines={headlines} />
      </div>

      <div
        className={s.carouselShell}
        onPointerEnter={pauseCarousel}
        onPointerLeave={resumeCarousel}
        onFocusCapture={handleCarouselFocus}
        onBlurCapture={handleCarouselBlur}
      >
        <div className={s.viewport} ref={emblaRef}>
          <div className={s.carouselTrack}>
            {repeatedCards.map((card, index) => {
              const originalIndex = index % cards.length

              return (
                <div
                  key={`${card.id}-${index}`}
                  className={s.slide}
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`Person ${originalIndex + 1} of ${cards.length}`}
                >
                  <PeopleHeroCardItem
                    card={card}
                    index={index}
                    isActive={index === activeCardIndex}
                    onActivate={() => setActiveCardIndex(index)}
                    onDeactivate={() => setActiveCardIndex(-1)}
                  />
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}
